import { ActionStatus } from 'thunkless';
import produce from 'immer';

import { OfficeHoursTypes } from '../actions/office-hours';

export interface OfficeHourException {
  id: number;
  exception_date: string;
  label: string | null;
}

export type DayName = 'sunday' | 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday';

export type OfficeHoursByDay = Record<DayName, string>;

// [display_label, activesupport_name (storage), iana_name (for browser auto-detect)]
export type ObservedZone = [string, string, string];

export interface OfficeHoursState {
  fetchStatus: ActionStatus;
  officeHours: OfficeHoursByDay | null;
  timezone: string | null;
  observedZones: ObservedZone[];
  agentsAvailableOfficeHours: boolean;
  currentlyOffHours: boolean;
  exceptions: OfficeHourException[];
  createExceptionStatus: ActionStatus;
  createExceptionError: string | null;
}

export const initialState: OfficeHoursState = {
  fetchStatus: null,
  officeHours: null,
  timezone: null,
  observedZones: [],
  agentsAvailableOfficeHours: false,
  currentlyOffHours: false,
  exceptions: [],
  createExceptionStatus: null,
  createExceptionError: null,
};

export const reducer = produce((state: OfficeHoursState, action: any) => {
  switch (action.type) {
    case OfficeHoursTypes.FETCH_OFFICE_HOURS_DATA_REQUEST: {
      state.fetchStatus = ActionStatus.BUSY;
      break;
    }
    case OfficeHoursTypes.FETCH_OFFICE_HOURS_DATA_SUCCESS: {
      const { data } = action.payload;
      state.fetchStatus = ActionStatus.SUCCESS;
      state.officeHours = data.office_hours;
      state.timezone = data.timezone;
      state.observedZones = data.observed_zones;
      state.agentsAvailableOfficeHours = data.agents_available_office_hours;
      state.currentlyOffHours = data.currently_off_hours;
      state.exceptions = data.office_hour_exceptions;
      break;
    }
    case OfficeHoursTypes.FETCH_OFFICE_HOURS_DATA_FAILURE: {
      state.fetchStatus = ActionStatus.FAILURE;
      break;
    }
    case OfficeHoursTypes.CREATE_EXCEPTION_REQUEST: {
      state.createExceptionStatus = ActionStatus.BUSY;
      state.createExceptionError = null;
      break;
    }
    case OfficeHoursTypes.CREATE_EXCEPTION_SUCCESS: {
      state.createExceptionStatus = ActionStatus.SUCCESS;
      // office_hour_exceptions#create returns the bare record (no { data } wrapper),
      // so read from action.payload directly.
      const exc: OfficeHourException = action.payload;
      if (!exc || !exc.exception_date) break;
      const insertAt = state.exceptions.findIndex(
        (e) => e.exception_date > exc.exception_date,
      );
      if (insertAt === -1) state.exceptions.push(exc);
      else state.exceptions.splice(insertAt, 0, exc);
      break;
    }
    case OfficeHoursTypes.CREATE_EXCEPTION_FAILURE: {
      state.createExceptionStatus = ActionStatus.FAILURE;
      const errors = action.payload?.error?.responseJSON?.errors;
      state.createExceptionError = Array.isArray(errors) && errors.length
        ? errors.join(', ')
        : 'Could not add date.';
      break;
    }
    case OfficeHoursTypes.CLEAR_CREATE_EXCEPTION_ERROR: {
      state.createExceptionError = null;
      break;
    }
    case OfficeHoursTypes.DESTROY_EXCEPTION_SUCCESS: {
      const { id } = action.meta;
      state.exceptions = state.exceptions.filter((e) => e.id !== id);
      break;
    }
    case OfficeHoursTypes.DESTROY_EXCEPTION_FAILURE: {
      // A 404 means the record is already gone on the server (e.g. stale tab,
      // concurrent delete). Reconcile by dropping the phantom row locally so
      // the UI doesn't diverge from the backend.
      if (action.payload?.error?.status === 404) {
        const { id } = action.meta;
        state.exceptions = state.exceptions.filter((e) => e.id !== id);
      }
      break;
    }
  }
}, initialState);
